import { useState } from 'react';
import { useSiteData } from '@/contexts/SiteDataContext';
import type { ContactInfo } from '@/contexts/SiteDataContext';

export default function ContactSection() {
  const { data, updateContact } = useSiteData();
  const [form, setForm] = useState<ContactInfo>({ ...data.contact });
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    updateContact(form);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const field = (label: string, key: keyof ContactInfo, type: string = 'text', hint?: string) => (
    <div>
      <label className="block text-sm font-semibold text-foreground-700 mb-1.5">{label}</label>
      {hint && <p className="text-xs text-foreground-400 mb-1.5">{hint}</p>}
      <input
        type={type}
        value={form[key] as string}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="w-full px-4 py-3 rounded-xl border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
      />
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-foreground-900 mb-1">Contact Details</h2>
        <p className="text-foreground-500 text-sm">Updates reflect in the footer, contact page, and everywhere the address appears.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="md:col-span-2">
          {field('Office Address', 'address')}
        </div>
        {field('Phone Number', 'phone', 'tel', 'Format: +91 81435 85288')}
        {field('Email Address', 'email', 'email')}
        {field('Helpline Number', 'helpline')}
        {field('Working Hours', 'hours')}
        <div className="md:col-span-2">
          {field('Google Maps Embed URL', 'mapUrl', 'text', 'Use format: https://maps.google.com/maps?q=LAT,LNG&z=17&output=embed')}
        </div>
      </div>

      {/* Live preview */}
      <div className="p-5 bg-primary-50 rounded-2xl border border-primary-100 space-y-2">
        <p className="text-xs font-bold text-primary-700 uppercase tracking-wider mb-3">Preview — Contact Card</p>
        <div className="flex items-start gap-2">
          <i className="ri-map-pin-2-fill text-primary-500 mt-0.5 text-sm"></i>
          <span className="text-foreground-700 text-sm">{form.address}</span>
        </div>
        <div className="flex items-center gap-2">
          <i className="ri-phone-fill text-primary-500 text-sm"></i>
          <a href={`tel:${form.phone}`} className="text-foreground-700 text-sm">{form.phone}</a>
        </div>
        <div className="flex items-center gap-2">
          <i className="ri-mail-fill text-primary-500 text-sm"></i>
          <a href={`mailto:${form.email}`} className="text-foreground-700 text-sm">{form.email}</a>
        </div>
        <div className="flex items-center gap-2">
          <i className="ri-time-line text-primary-500 text-sm"></i>
          <span className="text-foreground-700 text-sm">{form.hours}</span>
        </div>
      </div>

      {form.mapUrl && (
        <div className="rounded-xl overflow-hidden border border-background-200 h-48">
          <iframe src={form.mapUrl} width="100%" height="100%" style={{ border: 0 }} allowFullScreen title="Map preview" />
        </div>
      )}

      <button
        onClick={handleSave}
        className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-300 whitespace-nowrap ${
          saved ? 'bg-emerald-500 text-white' : 'bg-primary-500 text-white hover:bg-primary-600'
        }`}
      >
        <i className={saved ? 'ri-check-line' : 'ri-save-line'}></i>
        {saved ? 'Saved!' : 'Save Contact Details'}
      </button>
    </div>
  );
}